import { SelectionCard } from '@/features/Strategies/components/SelectionCard';
import { FilterExcludedCountBadge } from '../EligibilityRules/FilterExcludedCountBadge';

interface MultiSelectCardGroupProps {
  /** Section header label */
  label: string;
  /** Helper text shown next to the label (e.g. "Select one or more") */
  hint?: string;
  /** Options to render as selection cards */
  items: ReadonlyArray<{ id: string; label: string }>;
  /** Currently selected item IDs */
  selectedIds: Array<string>;
  /** Item IDs that should be disabled (non-interactive) */
  disabledIds?: Array<string>;
  /** Called when a card is toggled */
  onToggle: (id: string) => void;
  /** Securities this selector's deselections drop — renders the same
   *  "N excluded" badge as the sectors/groups/industries headers (hidden
   *  at 0). */
  excludedCount?: number;
  /** Number of grid columns. Defaults to 3. */
  numCols?: number;
  /** Minimum number of items that must remain selected. Defaults to 0. */
  minSelected?: number;
  /** Extra classes on each pill's label (e.g. font-medium for Cyclicality). */
  pillLabelClassName?: string;
}

/** Grid column class lookup. Keeps Tailwind classes static for purging. */
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

export function MultiSelectCardGroup({
  label,
  hint,
  items,
  selectedIds,
  disabledIds = [],
  onToggle,
  numCols = 3,
  minSelected = 0,
  pillLabelClassName,
  excludedCount,
}: MultiSelectCardGroupProps) {
  const gridClass = GRID_COLS[numCols] ?? 'grid-cols-3';
  // Defensive default — the TS type says `selectedIds: Array<string>`, but
  // callers occasionally pass undefined when the upstream store hasn't
  // hydrated (e.g. a freshly-picked universe whose scope/size mapping
  // wasn't in UNIVERSE_SCOPES). Without this guard, the page crashes
  // with "Cannot read properties of undefined (reading 'includes')"
  // and the user has to bail out to the dashboard.
  const safeSelected = selectedIds ?? [];
  const activeCount = items.filter(
    (item) => safeSelected.includes(item.id) && !disabledIds.includes(item.id),
  ).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {/* Figma: section title at 15px foreground (not primary), with the
            multi-select hint at text-xs muted. */}
        <span className="text-[15px] text-foreground font-normal">{label}</span>
        <span className="text-destructive">*</span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        {excludedCount != null && (
          <FilterExcludedCountBadge count={excludedCount} />
        )}
      </div>
      <div className={`grid ${gridClass} gap-2`}>
        {items.map((item) => {
          const isSelected = safeSelected.includes(item.id);
          const isDisabled = disabledIds.includes(item.id);
          const isLocked =
            isSelected && !isDisabled && activeCount <= minSelected;
          return (
            <SelectionCard
              key={item.id}
              label={item.label}
              isSelected={isSelected}
              isDisabled={isDisabled}
              isLocked={isLocked}
              compact
              labelClassName={pillLabelClassName}
              onClick={() => onToggle(item.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
